import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LS_SESSION = "caresenta_robo_session";
const LS_TOKEN = "caresenta_robo_token";
const LS_EMAIL = "caresenta_robo_email";

function uuid() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

function readStr(key) {
  try { return typeof window !== "undefined" ? window.localStorage.getItem(key) : null; }
  catch { return null; }
}
function writeStr(key, v) {
  try { window.localStorage.setItem(key, v); } catch {}
}
function clearKey(key) {
  try { window.localStorage.removeItem(key); } catch {}
}

function truncateEmail(e) {
  if (!e) return "pro";
  if (e.length <= 24) return e;
  const [local, domain] = e.split("@");
  if (!domain) return e.slice(0, 22) + "…";
  const shortLocal = local.length > 12 ? local.slice(0, 10) + "…" : local;
  return `${shortLocal}@${domain}`;
}

const SUGGESTIONS_FREE = [
  "How does the Clinical Note Scribe work?",
  "Which EHRs do you integrate with?",
  "What's the difference between the plans?",
  "How fast is onboarding?",
];
const SUGGESTIONS_PRO = [
  "Walk me through configuring Prior Auth for Humana",
  "How do I rotate my EHR OAuth credentials?",
  "Show me the webhook payload for refill.requested",
  "Set up physician auto-approval for refills",
];

export default function RoboWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [messages, setMessages] = useState([]); // {role: 'user'|'robo', text}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const listRef = useRef(null);

  const isPro = Boolean(token);

  // Init session + restore subscriber token on mount
  useEffect(() => {
    let s = readStr(LS_SESSION);
    if (!s) { s = uuid(); writeStr(LS_SESSION, s); }
    setSessionId(s);
    const t = readStr(LS_TOKEN);
    const e = readStr(LS_EMAIL);
    if (t) { setToken(t); setEmail(e || ""); }
  }, []);

  // Greet on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      const greet = isPro
        ? `ROBO Pro online. Ask me anything about configuring, integrating, or extending CareSenta — I'll answer fast and in full.`
        : `Hi, I'm ROBO — CareSenta's concierge. I answer How-To questions in a sentence or two. For the full walkthrough, unlock ROBO Pro (free) below.`;
      setMessages([{ role: "robo", text: greet }]);
    }
  }, [open, isPro, messages.length]);

  // Auto-scroll
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const res = await api.post("/robo/chat", {
        session_id: sessionId,
        message: msg,
        subscriber_token: token || undefined,
      });
      setMessages((m) => [...m, { role: "robo", text: res.data.reply, is_pro: res.data.is_pro }]);
      if (res.data.is_pro !== isPro && token && !res.data.is_pro) {
        // token rejected — clear
        clearKey(LS_TOKEN); clearKey(LS_EMAIL);
        setToken(""); setEmail("");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "ROBO couldn't respond. Try again in a moment.";
      setMessages((m) => [...m, { role: "robo", text: typeof msg === "string" ? msg : "ROBO error" }]);
    } finally {
      setSending(false);
    }
  };

  const subscribe = async (emailVal, nameVal) => {
    try {
      const res = await api.post("/robo/subscribe", { email: emailVal, name: nameVal });
      writeStr(LS_TOKEN, res.data.id);
      writeStr(LS_EMAIL, res.data.email);
      setToken(res.data.id);
      setEmail(res.data.email);
      setUpsellOpen(false);
      toast.success("ROBO Pro unlocked. Fire away.");
      // Announce the upgrade in the chat
      setMessages((m) => [
        ...m,
        { role: "system", text: `ROBO Pro unlocked for ${res.data.email}. Detailed answers enabled.` },
      ]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't unlock ROBO Pro. Try again.";
      toast.error(typeof msg === "string" ? msg : "Subscribe failed");
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="robo-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] group inline-flex items-center gap-3 bg-[#0A140F] text-[#F8F7F4] px-5 py-3 hard-border-dark shadow-[0_6px_0_0_#0A140F] hover:translate-y-[-1px] transition-transform"
          aria-label="Ask ROBO"
        >
          <RoboMark size={24} />
          <span className="font-display font-bold text-sm">Ask ROBO</span>
          {isPro && (
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase bg-[#8B2B3A] text-[#F8F7F4] px-1.5 py-0.5">PRO</span>
          )}
          <span className="ticker-dot inline-block ml-1" aria-hidden />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="robo-panel"
          className="fixed bottom-5 right-5 z-[60] w-[min(400px,calc(100vw-2rem))] h-[min(620px,calc(100vh-2.5rem))] bg-[#F8F7F4] hard-border flex flex-col shadow-[0_12px_0_0_rgba(10,20,15,0.08)]"
        >
          {/* Header */}
          <div className="bg-[#0A140F] text-[#F8F7F4] p-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <RoboMark size={32} />
              <div className="min-w-0">
                <div className="font-display font-bold text-lg leading-none">ROBO{isPro ? " Pro" : ""}</div>
                <div className="mono-label text-[#8A9992] mt-1 truncate">
                  {isPro ? `Unlocked · ${truncateEmail(email)}` : "CareSenta concierge · free tier"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isPro && (
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase bg-[#8B2B3A] text-[#F8F7F4] px-2 py-1" data-testid="robo-pro-badge">PRO</span>
              )}
              <button
                onClick={() => setOpen(false)}
                className="font-mono text-xs text-[#8A9992] hover:text-[#F8F7F4] transition-colors px-2 py-1"
                aria-label="Close"
                data-testid="robo-close"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F8F7F4]" data-testid="robo-messages">
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} text={m.text} />
            ))}
            {sending && (
              <div className="flex gap-3">
                <RoboMark size={22} />
                <div className="font-mono text-xs text-[#4A5A52] mt-1">
                  <span className="blinking-cursor">ROBO is thinking</span>
                </div>
              </div>
            )}

            {/* Suggestion chips on first turn */}
            {messages.length <= 1 && !sending && (
              <div className="flex flex-wrap gap-2 pt-2">
                {(isPro ? SUGGESTIONS_PRO : SUGGESTIONS_FREE).map((q, i) => (
                  <button
                    key={i}
                    onClick={() => send(q)}
                    data-testid={`robo-suggestion-${i}`}
                    className="text-left text-xs border border-[#D6D2C4] bg-white px-3 py-2 hover:border-[#0A140F] hover:bg-[#E8F0EB] transition-colors max-w-full"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Upsell bar (only when free) */}
          {!isPro && (
            <div className="border-t border-[#D6D2C4] bg-[#E8F0EB] px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="mono-label text-[#8B2B3A]">Unlock detailed answers</div>
                <div className="text-xs text-[#4A5A52] mt-0.5 truncate">ROBO Pro answers fast and in full. Free.</div>
              </div>
              <button
                data-testid="robo-unlock-button"
                onClick={() => setUpsellOpen(true)}
                className="shrink-0 bg-[#8B2B3A] text-[#F8F7F4] px-3 py-2 text-xs font-medium hover:bg-[#5A1C26] transition-colors"
              >
                Unlock Pro →
              </button>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[#D6D2C4] bg-white flex items-center p-2 gap-2"
          >
            <input
              data-testid="robo-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isPro ? "Ask ROBO Pro anything…" : "Ask a How-To question…"}
              className="flex-1 px-3 py-2 font-mono text-sm bg-transparent focus:outline-none"
              disabled={sending}
              maxLength={1000}
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              data-testid="robo-send"
              className="bg-[#0A140F] text-[#F8F7F4] px-4 py-2 text-sm font-medium hover:bg-[#1A2E27] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </form>
        </div>
      )}

      {/* Upsell modal */}
      {upsellOpen && (
        <UnlockModal
          onClose={() => setUpsellOpen(false)}
          onSubmit={subscribe}
        />
      )}
    </>
  );
}

function MessageBubble({ role, text }) {
  if (role === "system") {
    return (
      <div data-testid="robo-msg-system" className="font-mono text-[11px] tracking-wider uppercase text-[#8B2B3A] text-center border-y border-[#D6D2C4] py-2">
        {text}
      </div>
    );
  }
  if (role === "user") {
    return (
      <div className="flex justify-end" data-testid="robo-msg-user">
        <div className="max-w-[85%] bg-[#0A140F] text-[#F8F7F4] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3" data-testid="robo-msg-robo">
      <RoboMark size={22} />
      <div className="flex-1 min-w-0 bg-white hard-border px-3.5 py-2.5 text-sm leading-relaxed text-[#0A140F] whitespace-pre-wrap break-words">
        {renderMarkdownLite(text)}
      </div>
    </div>
  );
}

function renderMarkdownLite(text) {
  // Bold **x** + newlines + inline code `x`
  const lines = text.split("\n");
  return lines.map((ln, i) => {
    const parts = [];
    let rest = ln;
    let idx = 0;
    while (rest.length > 0) {
      const boldMatch = rest.match(/\*\*(.+?)\*\*/);
      const codeMatch = rest.match(/`([^`]+)`/);
      const first = [boldMatch, codeMatch].filter(Boolean).sort((a, b) => a.index - b.index)[0];
      if (!first) {
        parts.push(<span key={idx++}>{rest}</span>);
        break;
      }
      if (first.index > 0) parts.push(<span key={idx++}>{rest.slice(0, first.index)}</span>);
      if (first === boldMatch) parts.push(<strong key={idx++} className="text-[#0A140F]">{first[1]}</strong>);
      else parts.push(<code key={idx++} className="font-mono text-[12px] bg-[#E8F0EB] px-1 py-0.5 text-[#0A140F]">{first[1]}</code>);
      rest = rest.slice(first.index + first[0].length);
    }
    return <div key={i}>{parts}</div>;
  });
}

function UnlockModal({ onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    await onSubmit(email, name || undefined);
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-[#0A140F]/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="robo-unlock-modal" onClick={onClose}>
      <div className="bg-[#F8F7F4] hard-border max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#D6D2C4]">
          <div className="flex items-center gap-3">
            <RoboMark size={34} />
            <div>
              <div className="mono-label text-[#8B2B3A]">ROBO Pro</div>
              <h3 className="font-display text-2xl leading-tight text-[#0A140F] mt-1">Get the detailed answer.</h3>
            </div>
          </div>
          <p className="mt-4 text-sm text-[#4A5A52] leading-relaxed">
            ROBO Pro answers in full — numbered steps, config paths, example payloads, API snippets. It's free while we're in early access.
          </p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <div className="mono-label text-[#4A5A52] mb-2">Work email *</div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="robo-unlock-email"
              autoFocus
              className="w-full bg-white border border-[#D6D2C4] px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#0A140F] focus:ring-1 focus:ring-[#0A140F]"
              placeholder="you@practice.health"
            />
          </label>
          <label className="block">
            <div className="mono-label text-[#4A5A52] mb-2">Name (optional)</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="robo-unlock-name"
              className="w-full bg-white border border-[#D6D2C4] px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#0A140F] focus:ring-1 focus:ring-[#0A140F]"
              placeholder="Dr. Lena Ortiz"
            />
          </label>
          <div className="flex items-center justify-between gap-3 pt-2">
            <button type="button" onClick={onClose} className="mono-label text-[#4A5A52] hover:text-[#0A140F]" data-testid="robo-unlock-cancel">
              Maybe later
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="robo-unlock-submit"
              className="bg-[#8B2B3A] text-[#F8F7F4] px-6 py-3 font-medium hover:bg-[#5A1C26] transition-colors disabled:opacity-60"
            >
              {loading ? "Unlocking…" : "Unlock ROBO Pro →"}
            </button>
          </div>
          <p className="font-mono text-[10px] tracking-wider text-[#8A9992] pt-2 border-t border-[#D6D2C4]">
            No password. No credit card. We never share your email. PHI never appears in ROBO chat.
          </p>
        </form>
      </div>
    </div>
  );
}

/** ROBO's own mark — a square "terminal face" with a single oxblood module indicator. */
function RoboMark({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" className="shrink-0" aria-hidden>
      <rect x="2" y="6" width="44" height="36" fill="none" stroke="currentColor" strokeWidth="3" />
      <rect x="10" y="16" width="7" height="7" fill="currentColor" />
      <rect x="31" y="16" width="7" height="7" fill="#8B2B3A" />
      <rect x="16" y="30" width="16" height="4" fill="currentColor" />
      <line x1="24" y1="6" x2="24" y2="1" stroke="currentColor" strokeWidth="3" />
      <rect x="21" y="0" width="6" height="2" fill="#8B2B3A" />
    </svg>
  );
}
